/**
 * Room Browser UI Component
 * Displays public rooms and provides room creation/joining functionality
 */

import { roomManager, type RoomInfo, type RoomSettings } from '../../multiplayer';
import { authService } from '../../auth';

/** Room browser configuration */
export interface RoomBrowserConfig {
  onJoinRoom: (roomInfo: RoomInfo) => void;
  onCreateRoom: (roomInfo: RoomInfo) => void;
  onError?: (error: string) => void;
}

/** Room Browser Component */
export class RoomBrowser {
  private container: HTMLElement;
  private config: RoomBrowserConfig;
  private rooms: RoomInfo[] = [];
  private isLoading = false;
  private isCreatingRoom = false;

  constructor(config: RoomBrowserConfig) {
    this.config = config;
    this.container = document.createElement('div');
    this.container.className = 'room-browser';
    this.render();
    this.loadRooms();
  }

  /** Get the container element */
  getElement(): HTMLElement {
    return this.container;
  }

  /** Load public rooms */
  async loadRooms(): Promise<void> {
    this.isLoading = true;
    this.render();

    try {
      this.rooms = await roomManager.listPublicRooms();
    } catch (error) {
      console.error('Failed to load rooms:', error);
      this.config.onError?.('Failed to load rooms');
    }

    this.isLoading = false;
    this.render();
  }

  /** Join a room by code */
  async joinByCode(code: string): Promise<void> {
    try {
      const roomInfo = await roomManager.joinRoom(code);
      if (roomInfo) {
        this.config.onJoinRoom(roomInfo);
      } else {
        this.config.onError?.('Room not found');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to join room';
      this.config.onError?.(message);
    }
  }

  /** Create a new room */
  async createRoom(settings: Partial<RoomSettings>): Promise<void> {
    if (this.isCreatingRoom) return;
    
    // Check if user is authenticated
    const user = authService.getCurrentUser();
    if (!user) {
      this.config.onError?.('Please sign in to create a room');
      return;
    }
    
    this.isCreatingRoom = true;
    this.render(); // Update UI to show loading state
    
    try {
      const roomInfo = await roomManager.createRoom(settings, user.id);
      this.isCreatingRoom = false;
      this.config.onCreateRoom(roomInfo);
    } catch (error) {
      this.isCreatingRoom = false;
      this.render(); // Re-render to remove loading state
      const message = error instanceof Error ? error.message : 'Failed to create room';
      this.config.onError?.(message);
    }
  }

  /** Render the component */
  private render(): void {
    const createBtnText = this.isCreatingRoom 
      ? '<span class="spinner-small"></span> Creating...' 
      : 'Create Room';
    const createBtnDisabled = this.isCreatingRoom ? 'disabled' : '';
    
    this.container.innerHTML = `
      <div class="room-browser-header">
        <h2>Multiplayer Rooms</h2>
        <div class="room-browser-actions">
          <button class="btn btn-primary" id="create-room-btn" ${createBtnDisabled}>${createBtnText}</button>
          <button class="btn btn-secondary" id="refresh-rooms-btn" ${this.isCreatingRoom ? 'disabled' : ''}>
            <span class="refresh-icon">↻</span> Refresh
          </button>
        </div>
      </div>

      <div class="join-by-code">
        <input 
          type="text" 
          id="room-code-input" 
          placeholder="Enter room code" 
          maxlength="6"
          class="room-code-input"
        />
        <button class="btn btn-primary" id="join-code-btn">Join</button>
      </div>

      <div class="room-list-container">
        ${this.isLoading ? this.renderLoading() : this.renderRoomList()}
      </div>

      <div class="create-room-modal" id="create-room-modal" style="display: none;">
        <div class="modal-overlay"></div>
        <div class="modal-content">
          <h3>Create New Room</h3>
          <form id="create-room-form">
            <div class="form-group">
              <label for="board-size">Board Size</label>
              <select id="board-size" name="boardSize">
                <option value="5">5×5 (Quick)</option>
                <option value="7">7×7 (Standard)</option>
                <option value="9">9×9 (Large)</option>
              </select>
            </div>
            <div class="form-group">
              <label>
                <input type="checkbox" id="is-public" name="isPublic" checked />
                Public Room (visible in lobby)
              </label>
            </div>
            <div class="form-group">
              <label for="max-spectators">Max Spectators</label>
              <input 
                type="number" 
                id="max-spectators" 
                name="maxSpectators" 
                value="10" 
                min="0" 
                max="50"
              />
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" id="cancel-create-btn">Cancel</button>
              <button type="submit" class="btn btn-primary">Create Room</button>
            </div>
          </form>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  /** Render loading state */
  private renderLoading(): string {
    return `
      <div class="room-list-loading">
        <div class="spinner"></div>
        <p>Loading rooms...</p>
      </div>
    `;
  }

  /** Render room list */
  private renderRoomList(): string {
    if (this.rooms.length === 0) {
      return `
        <div class="room-list-empty">
          <p>No public rooms available</p>
          <p class="hint">Create a new room or join by code</p>
        </div>
      `;
    }

    return `
      <div class="room-list">
        ${this.rooms.map(room => this.renderRoomCard(room)).join('')}
      </div>
    `;
  }

  /** Render a room card */
  private renderRoomCard(room: RoomInfo): string {
    const timeAgo = this.getTimeAgo(new Date(room.createdAt));
    
    return `
      <div class="room-card" data-room-code="${room.code}">
        <div class="room-card-header">
          <span class="room-code">${room.code}</span>
          <span class="room-players">${room.playerCount}/2 players</span>
        </div>
        <div class="room-card-details">
          <span class="room-board-size">${room.settings.boardSize}×${room.settings.boardSize} board</span>
          <span class="room-created">${timeAgo}</span>
        </div>
        <button class="btn btn-primary btn-join-room" data-room-code="${room.code}">
          Join Game
        </button>
      </div>
    `;
  }

  /** Get time ago string */
  private getTimeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }

  /** Attach event listeners */
  private attachEventListeners(): void {
    // Create room button
    const createBtn = this.container.querySelector('#create-room-btn');
    createBtn?.addEventListener('click', () => this.showCreateModal());

    // Refresh button
    const refreshBtn = this.container.querySelector('#refresh-rooms-btn');
    refreshBtn?.addEventListener('click', () => this.loadRooms());

    // Join by code
    const joinCodeBtn = this.container.querySelector('#join-code-btn');
    const codeInput = this.container.querySelector('#room-code-input') as HTMLInputElement;
    
    joinCodeBtn?.addEventListener('click', () => {
      if (codeInput.value.trim()) {
        this.joinByCode(codeInput.value.trim());
      }
    });

    codeInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && codeInput.value.trim()) {
        this.joinByCode(codeInput.value.trim());
      }
    });

    // Format room code input (uppercase)
    codeInput?.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase();
    });

    // Join room buttons in list
    const joinButtons = this.container.querySelectorAll('.btn-join-room');
    joinButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const code = (e.target as HTMLElement).dataset.roomCode;
        if (code) this.joinByCode(code);
      });
    });

    // Modal handlers
    const _modal = this.container.querySelector('#create-room-modal');
    const cancelBtn = this.container.querySelector('#cancel-create-btn');
    const overlay = this.container.querySelector('.modal-overlay');
    const form = this.container.querySelector('#create-room-form') as HTMLFormElement;

    cancelBtn?.addEventListener('click', () => this.hideCreateModal());
    overlay?.addEventListener('click', () => this.hideCreateModal());

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const settings: Partial<RoomSettings> = {
        boardSize: parseInt(formData.get('boardSize') as string) || 5,
        isPublic: formData.get('isPublic') === 'on',
        maxSpectators: parseInt(formData.get('maxSpectators') as string) || 10,
      };
      this.hideCreateModal();
      await this.createRoom(settings);
    });
  }

  /** Show create room modal */
  private showCreateModal(): void {
    const modal = this.container.querySelector('#create-room-modal') as HTMLElement;
    if (modal) {
      modal.style.display = 'flex';
    }
  }

  /** Hide create room modal */
  private hideCreateModal(): void {
    const modal = this.container.querySelector('#create-room-modal') as HTMLElement;
    if (modal) {
      modal.style.display = 'none';
    }
  }

  /** Destroy the component */
  destroy(): void {
    this.container.innerHTML = '';
  }
}

/** Create a room browser component */
export function createRoomBrowser(config: RoomBrowserConfig): RoomBrowser {
  return new RoomBrowser(config);
}
